import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError } from 'rxjs';
import { AppConfigService } from 'src/app/services/app-config.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';

import { DashboardFilters } from '../interface/dashboard-filters';
import {
  DashboardEvalResponse,
  DashboardIEPResponse,
  DashboardPlacementStatus,
  DashboardReferralResponse,
  DashboardResponse,
  HeaderResponse,
} from '../interface/dashboard-response';
import { PersonResponse } from '../interface/person';
import { DashboardRequest } from '../interface/dashboard-request';
import { ActionsResponse, StageActions } from '../interface/actions-response';
import { KPI } from '../interface/kpi-response';
import { CaseAssignment } from '../../../shared/interfaces/case-management';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { PlacementHistoryRequest } from 'src/app/shared/interfaces/placement-record';
import { ConsentsForReferral } from '../../consent/interfaces/consent';
import { SubmitCaseAssignModel } from '../models/submit-case-assign-model';
import {
  AllNotificationsResponse,
  NotificationSummaryResponse,
} from '../interface/get-all-notifications-response';
import { MarkPayload } from '../interface/mark-payload';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  config: IAppConfig;
  private reloadEvalGrid: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  reloadEvalGridData$: Observable<boolean> = this.reloadEvalGrid.asObservable();

  //TODO: Not sure? why null hasbeen using here will fix it later:
  private refreshDashboardGridSubject: BehaviorSubject<boolean | null> =
    new BehaviorSubject<boolean | null>(null);
  refreshDashboardGridSubject$: Observable<boolean | null> =
    this.refreshDashboardGridSubject.asObservable();

  private closeActionOnDashboardSubject: BehaviorSubject<boolean | null> =
    new BehaviorSubject<boolean | null>(false);
  closeActionOnDashboardSubject$: Observable<boolean | null> =
    this.closeActionOnDashboardSubject.asObservable();

  closeActionOnDashboardEvalSubject: BehaviorSubject<boolean | null> =
    new BehaviorSubject<boolean | null>(false);
  reloadDashboardSubject$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  reloadGrid: BehaviorSubject<boolean | null> = new BehaviorSubject<
    boolean | null
  >(false);
  reloadGridSubject$: Observable<boolean | null> =
    this.reloadGrid.asObservable();

  private searchData: string = '';
  private sortByNameData: string = '';
  private sortByDateData: string = '';
  private filterData: DashboardFilters;
  private caseLoadData: boolean = true;
  private caseProcessData: number = 0;
  private routed: boolean = false;
  isNotificationReloadData: boolean;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  saveDashboardData(
    searchData: string,
    sortByNameData: string,
    sortByDateData: string,
    filterData: DashboardFilters,
    caseLoadData: boolean,
    caseProcessData: number
  ): void {
    this.searchData = searchData;
    this.sortByNameData = sortByNameData;
    this.sortByDateData = sortByDateData;
    this.filterData = filterData;
    this.caseLoadData = caseLoadData;
    this.caseProcessData = caseProcessData;
  }

  saveRouted(routed: boolean): void {
    this.routed = routed;
  }

  getSearchData(): string {
    return this.searchData;
  }

  setNotificationReloadData(searchData: string): void {
    this.searchData = searchData;
    this.caseLoadData =  false; // As we are unsure if record is present in caseload, setting it to false
    this.isNotificationReloadData = true;
  }

  getNotificationReloadData(): boolean {
    const data = this.isNotificationReloadData;
    this.isNotificationReloadData = null; // clear after first use
    return data;
  }

  getSortByNameData(): string {
    return this.sortByNameData;
  }

  getSortByDateData(): string {
    return this.sortByDateData;
  }

  getFilterData(): DashboardFilters {
    return this.filterData;
  }

  getCaseLoadData(): boolean {
    return this.caseLoadData;
  }

  getCaseProcessdata(): number {
    return this.caseProcessData;
  }

  getRouted(): boolean {
    return this.routed;
  }

  getCloseActionOnDashboard(form): void {
    this.closeActionOnDashboardSubject.next(form);
  }

  getReloadDashboardGrid(form: boolean): void {
    this.refreshDashboardGridSubject.next(form);
  }

  getReloadGridDetails(form: boolean): void {
    this.reloadEvalGrid.next(form);
  }
  public getDashboardData(
    request: DashboardRequest
  ): Observable<DashboardResponse[]> {
    return this.http
      .post<DashboardResponse[]>(
        `${this.config.apiUrl}/Dashboard/Dashboard`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getStudentHeaderDetails(
    studentId: string
  ): Observable<HeaderResponse[]> {
    return this.http
      .get<HeaderResponse[]>(
        `${this.config.apiUrl}/Dashboard/StudentHeader/${studentId}`
      )
      .pipe(catchError(handleError));
  }

  public getReferralData(
    studentSk?: string
  ): Observable<DashboardReferralResponse[]> {
    return this.http
      .get<DashboardReferralResponse[]>(
        `${this.config.apiUrl}/Dashboard/Referral/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getIEPData(studentSk?: string): Observable<DashboardIEPResponse> {
    return this.http
      .get<DashboardIEPResponse>(
        `${this.config.apiUrl}/Dashboard/IEP/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getPlacementData(
    studentSk?: string
  ): Observable<DashboardPlacementStatus> {
    return this.http
      .get<DashboardPlacementStatus>(
        `${this.config.apiUrl}/Dashboard/Placement/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getEvalsData(studentSk?: string): Observable<DashboardEvalResponse> {
    return this.http
      .get<DashboardEvalResponse>(
        `${this.config.apiUrl}/Dashboard/Evaluation/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getActionItems(studentSk?: string): Observable<ActionsResponse> {
    return this.http
      .get<ActionsResponse>(
        `${this.config.apiUrl}/Dashboard/ActionItems/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getKPISummary(): Observable<KPI[]> {
    return this.http
      .get<KPI[]>(`${this.config.apiUrl}/Dashboard/KPISummary`)
      .pipe(catchError(handleError));
  }

  // TODO: wouldn't this method be in its own service file and not part of the dashboard-service file?
  public SubmitCaseAssignment(
    request: SubmitCaseAssignModel
  ): Observable<ApiResponse<CaseAssignment>> {
    return this.http
      .post<ApiResponse<CaseAssignment>>(
        `${this.config.apiUrl}/CaseAssignment/AddCase`,
        request
      )
      .pipe(catchError(handleError));
  }

  // TODO: wouldn't this method be in its own service file and not part of the dashboard-service file?
  public getCaseAssignment(): Observable<PersonResponse[]> {
    return this.http
      .get<PersonResponse[]>(`${this.config.apiUrl}/CaseAssignment/Staff`)
      .pipe(catchError(handleError));
  }

  public getEvalActionItems(
    studentSk?: string,
    evaluationSk?: string
  ): Observable<StageActions> {
    return this.http
      .get<StageActions>(
        `${this.config.apiUrl}/Dashboard/EvalActionItems/${evaluationSk}/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  // TODO: wouldn't this method be in its own service file and not part of the dashboard-service file?
  public getPlacementHistory(
    caseActionSk?: string
  ): Observable<PlacementHistoryRequest[]> {
    return this.http
      .get<PlacementHistoryRequest[]>(
        `${this.config.apiUrl}/PlacementFinder/PlacementRecord/${caseActionSk}`
      )
      .pipe(catchError(handleError));
  }

  public getConsentsForReferral(
    referralSK?: string
  ): Observable<ConsentsForReferral[]> {
    return this.http
      .get<ConsentsForReferral[]>(
        `${this.config.apiUrl}/Dashboard/GetConsentsForReferral/${referralSK}`
      )
      .pipe(catchError(handleError));
  }

  // TODO: shouldn't this method be in its own service file and not part of the dashboard-service file?
  // Was not able to find this method in the backend.
  public getReasonForDelayList(studentSk: string): Observable<any> {
    return this.http
      .get(
        `${this.config.apiUrl}/CaseClose/GETReasonForDelayByStudentSK?studentSK=${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getAllNotifications(
    pageNumber: number,
    pageSize: number,
    nycId: string
  ): Observable<AllNotificationsResponse[]> {
    return this.http
      .get<AllNotificationsResponse[]>(
        `${this.config.apiUrl}/AppNotification?pageNumber=${pageNumber}&pageSize=${pageSize}&studentId=${nycId}`
      )
      .pipe(catchError(handleError));
  }

  public markNotification(
    payload: MarkPayload[]
  ): Observable<ApiResponse<string>> {
    return this.http
      .post<ApiResponse<string>>(
        `${this.config.apiUrl}/AppNotification`,
        payload
      )
      .pipe(catchError(handleError));
  }

  public getNotificationSummary(): Observable<NotificationSummaryResponse> {
    return this.http
      .get<NotificationSummaryResponse>(
        `${this.config.apiUrl}/AppNotification/GetNotificationSummary`
      )
      .pipe(catchError(handleError));
  }

  public markAllRead(): Observable<HttpStatusCode> {
    return this.http
      .get<HttpStatusCode>(`${this.config.apiUrl}/AppNotification/MarkAllRead`)
      .pipe(catchError(handleError));
  }

  public getLoadedGridData(data: boolean): void {
    this.reloadGrid.next(data);
  }
}
